import { base64UrlToBytes, bytesToBase64Url } from './encoding';

export const RECOVERY_KIT_VERSION = 1 as const;
export const SECRET_BYTE_LENGTH = 32;

declare const recoverySecretBrand: unique symbol;
declare const backupIdBrand: unique symbol;

export type RecoverySecret = string & { readonly [recoverySecretBrand]: true };
export type BackupId = string & { readonly [backupIdBrand]: true };

export type RecoveryKit = {
  version: typeof RECOVERY_KIT_VERSION;
  backupId: BackupId;
  authVersion: number;
  authSecret: RecoverySecret;
  encryptionKey: RecoverySecret;
};

export type RandomBytes = (length: number) => Uint8Array;

const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;

function assertBackupId(value: string): asserts value is BackupId {
  if (!uuidPattern.test(value)) throw new Error('Invalid recovery kit');
}

function assertSecret(value: string): asserts value is RecoverySecret {
  try {
    if (base64UrlToBytes(value).byteLength !== SECRET_BYTE_LENGTH) {
      throw new Error('Invalid recovery kit');
    }
  } catch {
    throw new Error('Invalid recovery kit');
  }
}

export function webCryptoRandomBytes(length: number): Uint8Array<ArrayBuffer> {
  const value = new Uint8Array(length);
  crypto.getRandomValues(value);
  return value;
}

export function decodeRecoverySecret(secret: RecoverySecret): Uint8Array<ArrayBuffer> {
  const bytes = base64UrlToBytes(secret);
  if (bytes.byteLength !== SECRET_BYTE_LENGTH) throw new Error('Invalid secret length');
  return bytes;
}

export function generateRecoveryKit(
  options: {
    createBackupId?: () => string;
    randomBytes?: RandomBytes;
  } = {},
): RecoveryKit {
  const createBackupId = options.createBackupId ?? (() => crypto.randomUUID());
  const randomBytes = options.randomBytes ?? webCryptoRandomBytes;
  const backupId = createBackupId();
  assertBackupId(backupId);
  const authBytes = randomBytes(SECRET_BYTE_LENGTH);
  const encryptionBytes = randomBytes(SECRET_BYTE_LENGTH);
  if (
    authBytes.byteLength !== SECRET_BYTE_LENGTH ||
    encryptionBytes.byteLength !== SECRET_BYTE_LENGTH
  ) {
    throw new Error('Random source returned an invalid length');
  }
  const authSecret = bytesToBase64Url(authBytes);
  const encryptionKey = bytesToBase64Url(encryptionBytes);
  assertSecret(authSecret);
  assertSecret(encryptionKey);
  if (authSecret === encryptionKey) throw new Error('Random source produced duplicate secrets');
  return { version: RECOVERY_KIT_VERSION, backupId, authVersion: 1, authSecret, encryptionKey };
}

export function encodeRecoveryKit(kit: RecoveryKit): string {
  assertBackupId(kit.backupId);
  assertSecret(kit.authSecret);
  assertSecret(kit.encryptionKey);
  if (!Number.isSafeInteger(kit.authVersion) || kit.authVersion < 1) {
    throw new Error('Invalid recovery kit');
  }
  if (kit.authSecret === kit.encryptionKey) throw new Error('Invalid recovery kit');
  return `pnrk${RECOVERY_KIT_VERSION}.${kit.backupId}.${kit.authVersion}.${kit.authSecret}.${kit.encryptionKey}`;
}

export function parseRecoveryKit(value: string): RecoveryKit {
  const parts = value.trim().split('.');
  if (parts.length !== 5 || parts[0] !== `pnrk${RECOVERY_KIT_VERSION}`) {
    throw new Error('Invalid recovery kit');
  }
  const backupId = parts[1] ?? '';
  const authVersion = Number(parts[2]);
  const authSecret = parts[3] ?? '';
  const encryptionKey = parts[4] ?? '';
  assertBackupId(backupId);
  assertSecret(authSecret);
  assertSecret(encryptionKey);
  if (!Number.isSafeInteger(authVersion) || authVersion < 1)
    throw new Error('Invalid recovery kit');
  if (authSecret === encryptionKey) throw new Error('Invalid recovery kit');
  return {
    version: RECOVERY_KIT_VERSION,
    backupId,
    authVersion,
    authSecret,
    encryptionKey,
  };
}

export function rotateRecoverySecrets(
  kit: RecoveryKit,
  randomBytes: RandomBytes = webCryptoRandomBytes,
): RecoveryKit {
  const next = generateRecoveryKit({
    createBackupId: () => kit.backupId,
    randomBytes,
  });
  return { ...next, authVersion: kit.authVersion + 1 };
}
